import { Fragment, ReactNode } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'full';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  size?: ModalSize;
  children: ReactNode;
  closeOnOverlayClick?: boolean;
  showCloseButton?: boolean;
}

const sizeStyles: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  xxl: 'max-w-6xl',
  full: 'max-w-[calc(100vw-2rem)]',
};

// Modal sub-components
export interface ModalHeaderProps {
  title?: string;
  subtitle?: string;
  className?: string;
  children?: ReactNode;
}

export const ModalHeader = ({ title, subtitle, className, children }: ModalHeaderProps) => {
  return (
    <div className={clsx('px-6 pt-6 pb-4', className)}>
      <Dialog.Title
        as="h3"
        className="text-lg font-semibold leading-6"
        style={{ color: 'var(--color-text-primary)' }}
      >
        {children || title}
      </Dialog.Title>
      {subtitle && (
        <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          {subtitle}
        </p>
      )}
    </div>
  );
};

export interface ModalBodyProps {
  children: ReactNode;
  className?: string;
  noPadding?: boolean;
}

export const ModalBody = ({ children, className, noPadding = false }: ModalBodyProps) => {
  return (
    <div className={clsx(!noPadding && 'px-6 py-4', className)}>
      {children}
    </div>
  );
};

export interface ModalFooterProps {
  children: ReactNode;
  className?: string;
}

export const ModalFooter = ({ children, className }: ModalFooterProps) => {
  return (
    <div
      className={clsx('px-6 py-4 border-t rounded-b-lg', className)}
      style={{ backgroundColor: 'var(--color-bg-sunken)', borderColor: 'var(--color-border)' }}
    >
      <div className="flex justify-end gap-3">
        {children}
      </div>
    </div>
  );
};

export interface ModalTitleProps {
  children: ReactNode;
  className?: string;
}

export const ModalTitle = ({ children, className }: ModalTitleProps) => {
  return (
    <Dialog.Title
      as="h3"
      className={clsx('text-lg font-semibold leading-6', className)}
      style={{ color: 'var(--color-text-primary)' }}
    >
      {children}
    </Dialog.Title>
  );
};

const ModalComponent = ({
  isOpen,
  onClose,
  size = 'md',
  children,
  closeOnOverlayClick = true,
  showCloseButton = true,
}: ModalProps) => {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={closeOnOverlayClick ? onClose : () => {}}
      >
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </Transition.Child>

        {/* Modal container */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={clsx(
                  'w-full transform overflow-hidden rounded-lg text-left align-middle shadow-modal transition-all card',
                  sizeStyles[size]
                )}
              >
                {showCloseButton && (
                  <button
                    onClick={onClose}
                    className="absolute right-4 top-4 rounded-lg p-1 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                    style={{ color: 'var(--color-text-muted)' }}
                    aria-label="Close modal"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                )}
                {children}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

// Attach sub-components to Modal
export const Modal = Object.assign(ModalComponent, {
  Header: ModalHeader,
  Body: ModalBody,
  Footer: ModalFooter,
  Title: ModalTitle,
});
